/**
 * Canonical AGENZ copy, transcribed verbatim from docs/A gen Z.pptx.
 *
 * This is the seed source of truth. Once seeded, the admin owns every field —
 * editing here changes nothing on a running site. Re-running the seed is
 * idempotent (upsert by slug), so it will not clobber admin edits to other
 * fields, but it will reset the fields it manages.
 */

export const BRAND = {
  name: 'AGENZ',
  legalName: 'A gen Z Agency',
  year: 2026,
  tagline: 'Built to Perform.',
  heroLine: 'Built to make brands move.',
  heroSub:
    'We shape ideas into meaningful strategy, and transform content into real impact.',
  coverLine: 'Built to be noticed.',
  coverSub: 'Feels different for a reason.',
  closingLine: "If it works, it's AGENZ.",
  closingSub: "Let's build something people actually stop scrolling for.",
};

export const ABOUT = {
  eyebrow: 'About A Gen Z — Who We Are',
  body:
    'AGENZ is a creative and performance team working with brands to shape how ' +
    'they show up and grow online. We turn ideas into content that feels clear, ' +
    'consistent, and built with purpose, focusing on what actually matters for ' +
    'the brand without overcomplicating the process.',
};

export const MISSION = {
  eyebrow: 'Mission',
  headline: 'Built to Perform.',
  body:
    'Every decision starts with strategy. Every idea has a purpose. Every ' +
    'execution is built to move the brand forward. We work as an extension of ' +
    "your team, taking ownership like it's our own business.",
};

export const VISION = {
  eyebrow: 'Vision',
  headline: 'To set a new standard for how brands and agencies grow together.',
  body:
    'We see a future where agencies are trusted partners, not just service ' +
    'providers. A future where strategy comes first, collaboration feels ' +
    'natural, and growth is built together.',
};

/** Slide 7 — the five principles. */
export const MINDSET = {
  eyebrow: 'The AGENZ Mindset',
  headline:
    "Content isn't just what gets posted. It's how a brand builds meaning over time.",
  principles: [
    'Good content starts with clarity, not visuals.',
    'Strategy gives direction. Execution makes it happen.',
    'Content works better as a connected system, not a collection of random ideas.',
    "Every piece has a purpose, whether it's building awareness, driving engagement, or encouraging action.",
    'Simple thinking leads to better decisions. Better decisions lead to better marketing.',
  ],
};

/**
 * Slides 8–11 — all fourteen services, in deck order.
 * `icon` maps to the sparkle marks lifted from the deck's SVG assets.
 */
export const SERVICES = [
  {
    slug: 'digital-marketing-strategy',
    title: 'Digital Marketing Strategy',
    summary:
      'Turning market insights, audience behavior, and business goals into a clear plan for growth.',
    icon: 'burst',
    isFeatured: true,
  },
  {
    slug: 'social-media-management',
    title: 'Social Media Management',
    summary:
      'Managing daily platform activity with a structured approach that keeps the brand consistent, active, and connected to its audience.',
    icon: 'sparkle',
    isFeatured: true,
    meta: 'Facebook · Instagram · TikTok',
  },
  {
    slug: 'content-strategy-calendar',
    title: 'Content Strategy & Monthly Social Media Calendar',
    summary:
      'Organizing content around clear objectives to ensure every post supports a bigger goal, not just a publishing schedule.',
    icon: 'asterisk',
    isFeatured: true,
  },
  {
    slug: 'creative-direction',
    title: 'Creative Direction for Online',
    summary:
      'Shaping how the brand looks, feels, and communicates to create a consistent and recognizable presence online.',
    icon: 'star',
    isFeatured: true,
  },
  {
    slug: 'audience-building-campaigns',
    title: 'Audience Building & Campaign Management',
    summary:
      'Reaching the right audience through focused campaigns that connect the brand with people who are actually relevant to its goals.',
    icon: 'burst',
  },
  {
    slug: 'brand-monitoring',
    title: 'Brand Monitoring',
    summary:
      'Tracking brand performance, audience feedback, and market activity to guide clearer marketing and content decisions.',
    icon: 'sparkle',
  },
  {
    slug: 'content-writing',
    title: 'Content Writing',
    summary:
      "Writing content that matches the brand's tone and communicates ideas clearly in both languages without losing meaning or consistency.",
    icon: 'asterisk',
    meta: 'English & Arabic',
  },
  {
    slug: 'social-media-design-templates',
    title: 'Social Media Design Templates',
    summary:
      'Creating a consistent visual system for monthly content that keeps the brand identity stable across all posts and formats.',
    icon: 'star',
    meta: 'Monthly',
  },
  {
    slug: 'content-production',
    title: 'Content Production',
    summary:
      "Producing different types of content that match the brand's needs across visuals and video, focused on clarity, quality, and consistency in execution.",
    icon: 'burst',
    isFeatured: true,
    meta: 'Design · Photos · GIF · Motion · Video Editing',
  },
  {
    slug: 'social-media-posting',
    title: 'Social Media Posting',
    summary:
      'Keeping the brand active on social media through consistent posting that stays aligned with its content direction.',
    icon: 'sparkle',
    meta: '7–8 posts / week',
  },
  {
    slug: 'account-management',
    title: 'Account Management & Direction',
    summary:
      "Overseeing the day-to-day execution of the account to ensure content, communication, and overall direction stay aligned with the brand's strategy.",
    icon: 'asterisk',
  },
  {
    slug: 'kpi-tracking-reports',
    title: 'KPI Tracking & Monthly Performance Reports',
    summary:
      'Measuring content and campaign performance against defined goals, then turning the data into clear monthly insights that guide future improvements.',
    icon: 'star',
    isFeatured: true,
  },
  {
    slug: 'media-buying-forecasting',
    title: 'Media Buying Forecasting Plan',
    summary:
      'Planning paid media direction by estimating budgets, expected results, and campaign needs to support more controlled and efficient ad spending.',
    icon: 'burst',
  },
  {
    slug: 'mobile-shooting',
    title: 'Mobile Shooting',
    summary:
      'Producing short-form video content through mobile shooting to capture real, fast-moving content that fits social media trends and brand needs.',
    icon: 'sparkle',
    meta: '15–20 Reels / Month',
  },
];

/** Slide 12 — how we work. */
export const PROCESS = {
  eyebrow: 'How We Work (Our Process)',
  steps: [
    {
      number: '01',
      title: 'Understanding the brand',
      body: 'Getting clear on the brand, its audience, and where it currently stands.',
    },
    {
      number: '02',
      title: 'Research & direction',
      body: 'Studying the market and competitors to shape a clear content and creative direction.',
    },
    {
      number: '03',
      title: 'Planning & structure',
      body: 'Building a content system where every piece works as part of a connected plan, not isolated posts.',
    },
    {
      number: '04',
      title: 'Execution & improvement',
      body: 'Producing content, tracking performance, and refining based on what works.',
    },
  ],
};

/** Slide 13. */
export const CREATIVE_EXECUTION = {
  eyebrow: 'Creative Execution',
  body:
    'Creative execution turns ideas into real content across formats like ' +
    'visuals, motion, video, and short-form reels. It connects creative ' +
    'direction with clear marketing intent and adapts each idea into formats ' +
    'that work across social media while keeping a consistent brand identity ' +
    'and focusing on real performance, not just appearance.',
};

/** Slide 14. */
export const WHY_AGENZ = {
  eyebrow: 'Why AGENZ',
  headline: 'Why not AGENZ?',
  points: [
    'You become one of our family. We help your brand become more known, stronger, and better.',
    "Most likely, once you start working with us, you'll ask yourself: where have you been all this time?",
    "Because you'll find us smarter, sharper, and one of the coolest agencies you'll ever work with.",
    'We are like you… and like the brand you want to build.',
  ],
};

/** Header/footer navigation seeded on first run; editable in admin afterwards. */
export const NAVIGATION = [
  { label: 'Work', href: '/work', location: 'header', order: 1 },
  { label: 'Services', href: '/services', location: 'header', order: 2 },
  { label: 'About', href: '/about', location: 'header', order: 3 },
  { label: 'Process', href: '/process', location: 'header', order: 4 },
  // Contact is deliberately absent from the header — the 'Let's talk' button
  // beside the nav already leads there, and two links to one page dilutes it.
  { label: 'Work', href: '/work', location: 'footer', order: 1 },
  { label: 'Services', href: '/services', location: 'footer', order: 2 },
  { label: 'About', href: '/about', location: 'footer', order: 3 },
  { label: 'Privacy', href: '/privacy', location: 'footer', order: 4 },
];

/** Budget bands offered on the contact form. */
export const BUDGET_BANDS = [
  'Under $5k / month',
  '$5k – $10k / month',
  '$10k – $25k / month',
  '$25k+ / month',
  'Not sure yet',
];
